import {
  Area,
  ComposedChart,
  CartesianGrid,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ArrowDownRight, ArrowUpRight, Minus, TrendingUp } from "lucide-react";

const RED = "hsl(0 72% 45%)";
const GREEN = "hsl(152 55% 38%)";

const crore = (v) => `₹${(Number(v || 0) / 10000000).toFixed(2)} Cr`;
const rupees = (v) =>
  v === null || v === undefined ? "—" : `₹${Math.round(Number(v)).toLocaleString("en-IN")}`;

/** Which way the money has moved, said in one line an officer can act on. */
export const Movement = ({ movement, what = "Pending payment", testId }) => {
  if (!movement?.uploads) return null;
  const up = movement.change > 0;
  const flat = Math.round(movement.change) === 0;
  const Icon = flat ? Minus : up ? ArrowUpRight : ArrowDownRight;
  const colour = flat ? undefined : up ? RED : GREEN;
  return (
    <div className="flex flex-wrap items-center gap-2 text-[11px]" data-testid={testId}>
      <span className="flex items-center gap-1 font-semibold" style={colour ? { color: colour } : undefined}>
        <Icon className="h-3.5 w-3.5" />
        {flat
          ? `${what} unchanged since ${movement.from_label}`
          : `${what} ${up ? "up" : "down"} ${rupees(Math.abs(movement.change))} since ${movement.from_label}`}
      </span>
      <span className="text-muted-foreground">
        {movement.since_first
          ? `· ${movement.since_first > 0 ? "up" : "down"} ${rupees(
              Math.abs(movement.since_first),
            )} across ${movement.uploads} upload(s) since ${movement.first_label}`
          : `· level across ${movement.uploads} upload(s)`}
      </span>
    </div>
  );
};

const Box = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="border border-foreground bg-card p-3 text-xs">
      <div className="font-display text-sm font-bold">{label}</div>
      {row.file_name && <div className="mt-0.5 text-[11px] text-muted-foreground">{row.file_name}</div>}
      <div className="mt-2 space-y-0.5">
        <div className="flex items-center justify-between gap-6">
          <span style={{ color: RED }}>Pending</span>
          <span className="font-data">{rupees(row.pending_amount)}</span>
        </div>
        <div className="flex items-center justify-between gap-6">
          <span style={{ color: GREEN }}>Cleared</span>
          <span className="font-data">{rupees(row.cleared_amount)}</span>
        </div>
        {row.pending_count !== undefined && row.pending_count !== null && (
          <div className="flex items-center justify-between gap-6 text-muted-foreground">
            <span>Invoices pending</span>
            <span className="font-data">{row.pending_count}</span>
          </div>
        )}
        {row.oldest_age ? (
          <div className="flex items-center justify-between gap-6 text-muted-foreground">
            <span>Oldest</span>
            <span className="font-data">{row.oldest_age} days</span>
          </div>
        ) : null}
      </div>
      {row.uploaded_by && (
        <div className="mt-2 text-[10px] text-muted-foreground">brought in by {row.uploaded_by}</div>
      )}
    </div>
  );
};

/** The pending amount, upload after upload — so a build-up shows before it becomes a problem. */
export const InvoiceTrend = ({ trend, height = 260, testId = "invoice-trend" }) => {
  const points = trend?.points || [];
  if (points.length < 2)
    return (
      <div className="border border-border bg-card p-5" data-testid={`${testId}-waiting`}>
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-primary" />
          <div className="label-caps text-primary">How the pending amount is moving</div>
        </div>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Only one tracker has been brought in so far, so there is nothing to compare it with yet.
          The line starts from the next upload — and every upload after that adds a point, so a
          build-up of pending payment shows early.
        </p>
      </div>
    );

  return (
    <div className="border border-border bg-card" data-testid={testId}>
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-border p-5">
        <div>
          <div className="label-caps text-primary">Upload after upload</div>
          <h3 className="font-display text-xl font-bold tracking-tight">
            How the pending amount is moving
          </h3>
          <p className="mt-1 text-xs text-muted-foreground">
            One point per tracker brought in, in the order they arrived. Rising means invoices are
            being raised faster than Finance is clearing them.
          </p>
        </div>
        <div className="text-right">
          <div className="font-data text-2xl font-bold" style={{ color: RED }}>
            {rupees(points[points.length - 1].pending_amount)}
          </div>
          <div className="font-data text-[10px] uppercase tracking-widest text-muted-foreground">
            pending now
          </div>
        </div>
      </div>
      <div className="p-4">
        <div className="h-[260px] w-full min-w-[280px]">
          <ResponsiveContainer width="100%" height={height} minWidth={280} debounce={40}>
            <ComposedChart data={points} margin={{ top: 8, right: 8, left: 6, bottom: 0 }}>
              <defs>
                <linearGradient id="pendingFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={RED} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={RED} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="hsl(var(--border))" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }}
                stroke="hsl(var(--muted-foreground))"
              />
              <YAxis
                tickFormatter={crore}
                width={78}
                tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }}
                stroke="hsl(var(--muted-foreground))"
              />
              <Tooltip content={<Box />} />
              <Area
                type="monotone"
                dataKey="pending_amount"
                name="Pending"
                stroke={RED}
                strokeWidth={2}
                fill="url(#pendingFill)"
                dot={{ r: 3 }}
              />
              <Line
                type="monotone"
                dataKey="cleared_amount"
                name="Cleared"
                stroke={GREEN}
                strokeWidth={2}
                dot={{ r: 2 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-3 border-t border-border pt-3">
          <Movement movement={trend.movement} testId="invoice-trend-movement" />
          <p className="mt-1 text-[11px] text-muted-foreground">
            Highest it has been: {rupees(trend.movement?.worst)} · the amounts on this line are the
            portal's own, added up from each file's invoice rows.
          </p>
        </div>
      </div>
    </div>
  );
};

/** The same story for one unit, small enough to sit inside its deep-dive. */
export const EntityTrend = ({ trend, entity }) => {
  const mine = trend?.entities?.[entity];
  const points = mine?.series || [];
  if (points.length < 2) return null;
  return (
    <div className="mt-5 border border-border bg-card p-4" data-testid="entity-trend">
      <div className="label-caps text-primary">How this unit has moved</div>
      <div className="mt-3 h-[140px] w-full min-w-[240px]">
        <ResponsiveContainer width="100%" height={140} minWidth={240} debounce={40}>
          <ComposedChart data={points} margin={{ top: 4, right: 6, left: 4, bottom: 0 }}>
            <CartesianGrid stroke="hsl(var(--border))" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 9, fontFamily: "IBM Plex Mono" }}
              stroke="hsl(var(--muted-foreground))"
            />
            <YAxis
              tickFormatter={crore}
              width={70}
              tick={{ fontSize: 9, fontFamily: "IBM Plex Mono" }}
              stroke="hsl(var(--muted-foreground))"
            />
            <Tooltip content={<Box />} />
            <Line
              type="monotone"
              dataKey="pending_amount"
              name="Pending"
              stroke={RED}
              strokeWidth={2}
              dot={{ r: 3 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-2">
        <Movement movement={mine.movement} what="This unit's pending payment" testId="entity-trend-movement" />
      </div>
    </div>
  );
};
